import React, { useCallback } from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import { formatNativePrice } from "../../../../utils/numbers";
import { MAX_QUOTE_FRACTION_DIGITS } from "../../../Investments/constants";
import {
  InstrumentFixDraft,
  InstrumentFixEditor,
  InstrumentFixSection,
  ManualPriceEntry,
  withFixSection,
} from "../../components/InstrumentEditor";
import { DraftNotice } from "../../styles";
import { formatMarketDate } from "../../utils/format";
import { NO_PRICE_LABEL } from "../constants";
import { useInstrumentCatalogSession } from "../InstrumentCatalogProvider";
import { DetailActions, HiddenRowNotice } from "../styles";
import { InstrumentDetailProps } from "../types";

export const InstrumentDetail: React.FC<InstrumentDetailProps> = ({
  instrument,
  edits,
  isHidden,
  isFiltered,
  isStacked,
  onClearFilters,
  onClose,
  onBackToList,
}) => {
  const { id, symbol, nativeCurrency, lastPrice } = instrument;
  const { drafts, errors, updateDraft, clearErrors } =
    useInstrumentCatalogSession();
  const { isSaving, revision, resetEditor } = edits;
  const draft = drafts[id] ?? null;

  const handleDraftChange = useCallback(
    <S extends InstrumentFixSection>(
      section: S,
      value: InstrumentFixDraft[S] | null,
    ) => updateDraft(id, (current) => withFixSection(current, section, value)),
    [id, updateDraft],
  );
  const handleDiscard = () => {
    updateDraft(id, () => null);
    clearErrors(id);
    resetEditor();
  };

  const handleSaveCountry = (sourceCountry: string | null) =>
    edits.saveCountry(instrument, sourceCountry);
  const handleRepairListing = (yahooSymbol: string) =>
    edits.repair(instrument, yahooSymbol);
  const handleUseManualPricing = (entry: ManualPriceEntry) =>
    edits.applyManualPricing(instrument, entry);
  const handleUseAutomaticPricing = () =>
    edits.restoreAutomaticPricing(instrument);

  const notice = draft && (
    <DraftNotice>
      Unsaved draft · kept while browsing
      <Button
        type={ButtonTypes.link}
        disabled={isSaving}
        onClick={handleDiscard}
      >
        Discard draft
      </Button>
    </DraftNotice>
  );
  const lastPriceLabel = lastPrice
    ? `${formatNativePrice(lastPrice.priceNative, MAX_QUOTE_FRACTION_DIGITS)} ${nativeCurrency} · ${formatMarketDate(lastPrice.date)}`
    : NO_PRICE_LABEL;
  // Keyed on what the sections prefill from, not bumped on save: a remount before the refetch lands would read the old values and show a phantom change.
  const editorKey = [
    id,
    revision,
    instrument.sourceCountry,
    instrument.yahooSymbol,
    instrument.manualPricingOnly,
  ].join(":");

  return (
    <>
      <DetailActions>
        {isStacked && (
          <Button type={ButtonTypes.link} size="small" onClick={onBackToList}>
            Back to instruments
          </Button>
        )}
        <Button
          type={ButtonTypes.cancel}
          size="small"
          disabled={isSaving}
          onClick={onClose}
        >
          Close editor
        </Button>
      </DetailActions>
      {isHidden && (
        <HiddenRowNotice role="status" $tone="unknown">
          {`${symbol} isn't in the current list. Its editor stays open.`}
          {isFiltered && (
            <Button
              type={ButtonTypes.link}
              size="small"
              onClick={onClearFilters}
            >
              Clear filters
            </Button>
          )}
        </HiddenRowNotice>
      )}
      <InstrumentFixEditor
        key={editorKey}
        instrument={instrument}
        needsPricing
        label={`Edit ${symbol}`}
        lastPriceLabel={lastPriceLabel}
        isSubmitting={isSaving}
        errors={errors[id] ?? {}}
        drafts={draft}
        notice={notice}
        onDraftChange={handleDraftChange}
        onSaveCountry={handleSaveCountry}
        onRepairListing={handleRepairListing}
        onUseManualPricing={handleUseManualPricing}
        onUseAutomaticPricing={handleUseAutomaticPricing}
      />
    </>
  );
};
